import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

// Reason groups — separate real earned commission from opening balances,
// migrated data, redemptions, and adjustments so the admin never reads an
// OPENING_BALANCE entry as "earned commission".
const REASON_GROUPS = {
  COMMISSION_EARNED: { label: 'Earned commission', bucket: 'earned', color: 'text-emerald-700' },
  OPENING_BALANCE:   { label: 'Opening / migrated', bucket: 'opening', color: 'text-slate-700' },
  MIGRATION:         { label: 'Opening / migrated', bucket: 'opening', color: 'text-slate-700' },
  SAMPLES_REDEEMED:          { label: 'Redeemed', bucket: 'redeemed', color: 'text-amber-700' },
  ORDER_PAID_WITH_CREDITS:   { label: 'Redeemed', bucket: 'redeemed', color: 'text-amber-700' },
  MANUAL_ADJUSTMENT: { label: 'Adjustment', bucket: 'adjustment', color: 'text-indigo-700' },
  REVERSAL:          { label: 'Reversal', bucket: 'adjustment', color: 'text-rose-700' },
};
const REASON_OPTIONS = Object.keys(REASON_GROUPS);
const TYPE_OPTIONS = ['EARNED', 'REDEEMED', 'ADJUSTED'];
const SOURCE_OPTIONS = ['admin', 'herbosoft'];
const STATUS_OPTIONS = ['CONFIRMED', 'DRAFT', 'VOID'];

const emptyForm = () => ({
  amount: '', effectiveDate: '', reason: '', type: '',
  note: '', sourceReference: '', sourceSystem: 'admin', status: 'CONFIRMED',
});

export default function AdminCredits() {
  const [persons, setPersons] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    adminApi.listB2B().then(r => setPersons(r.data)).catch(() => setPersons([]));
  }, []);

  // Deep-link: /admin/credits?personId=... preselects the prescriber. Runs
  // once after the list is loaded so the ledger fetch has a valid target.
  useEffect(() => {
    const preselect = searchParams.get('personId');
    if (preselect && !selectedId && persons.length) {
      loadLedger(preselect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons, searchParams]);

  const loadLedger = async (id) => {
    setLoadingLedger(true);
    setSelectedId(id);
    setLedger(null);
    try {
      const { data } = await adminApi.getB2BCredits(id);
      setLedger(data);
    } catch (e) {
      toast.error('Failed to load credits', { description: e?.response?.data?.detail });
    } finally { setLoadingLedger(false); }
  };

  const filteredPersons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return persons;
    return persons.filter(p =>
      (p.firstName || '').toLowerCase().includes(q) ||
      (p.lastName || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.org?.companyName || '').toLowerCase().includes(q));
  }, [persons, search]);

  const buckets = useMemo(() => {
    const b = { earned: 0, opening: 0, redeemed: 0, adjustment: 0, other: 0 };
    for (const tx of ledger?.transactions || []) {
      if (tx.status === 'VOID') continue;
      const amount = Number(tx.amount) || 0;
      const bucket = REASON_GROUPS[tx.reason]?.bucket || 'other';
      b[bucket] += amount;
    }
    return Object.fromEntries(Object.entries(b).map(([k, v]) => [k, Math.round(v * 100) / 100]));
  }, [ledger]);

  const sortedTx = useMemo(() => {
    const txs = [...(ledger?.transactions || [])];
    txs.sort((a, b) => (b.effectiveDate || b.created_at || '').localeCompare(a.effectiveDate || a.created_at || ''));
    return txs;
  }, [ledger]);

  const submitEntry = async (e) => {
    e.preventDefault();
    // Defensive frontend validation mirroring the backend rules
    if (!form.amount) return toast.error('amount required');
    if (!form.effectiveDate) return toast.error('effectiveDate required');
    if (!form.reason) return toast.error('reason required');
    if (!form.note && !form.sourceReference) return toast.error('note or sourceReference required');

    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] == null) delete payload[k]; });
      await adminApi.createB2BCreditEntry(selectedId, payload);
      toast.success('Ledger entry added');
      setForm(emptyForm());
      await loadLedger(selectedId);
    } catch (err) {
      toast.error('Create failed', { description: err?.response?.data?.detail });
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-credits" className="p-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      {/* Person picker */}
      <aside className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Prescribers</h2>
        <input
          data-testid="credits-search"
          type="text" placeholder="Search name, email, company"
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full px-3 py-1.5 text-sm rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <div className="space-y-1 max-h-[70vh] overflow-auto">
          {filteredPersons.map(p => (
            <button
              key={p._id}
              data-testid={`credits-person-${p._id}`}
              onClick={() => loadLedger(p._id)}
              className={`w-full text-left px-3 py-2 rounded text-sm ${selectedId === p._id ? 'bg-slate-800 text-white' : 'bg-white hover:bg-slate-50 border border-gray-200'}`}
            >
              <div className="font-semibold">{p.firstName} {p.lastName}</div>
              <div className="text-xs opacity-70">{p.org?.companyName || p.email || '—'}</div>
            </button>
          ))}
          {!filteredPersons.length && <p className="text-sm text-gray-400 italic px-2">No matches</p>}
        </div>
      </aside>

      {/* Ledger detail */}
      <main>
        {!selectedId && <div className="text-gray-500 text-sm">Select a prescriber to view their credit ledger.</div>}

        {selectedId && loadingLedger && <div className="text-gray-500">Loading…</div>}

        {selectedId && ledger && (
          <div className="space-y-6">
            {/* Summary cards with proper bucket labels */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryCard label="Confirmed balance" value={ledger.balance} primary />
              <SummaryCard label="Earned commission" value={buckets.earned} />
              <SummaryCard label="Opening / migrated" value={buckets.opening} />
              <SummaryCard label="Redeemed" value={buckets.redeemed} negative />
              <SummaryCard label="Adjustments" value={buckets.adjustment} />
            </div>

            {/* Period summary */}
            {ledger.summary?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Per period</h3>
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 uppercase"><tr>
                    <th className="py-1 text-left">Period</th>
                    <th className="text-right">Earned comm.</th>
                    <th className="text-right">Opening / migr.</th>
                    <th className="text-right">Redeemed</th>
                    <th className="text-right">Adjustments</th>
                    <th className="text-right">Uncategorized</th>
                    <th className="text-right">Net</th>
                    <th className="text-right">#</th>
                  </tr></thead>
                  <tbody>
                    {ledger.summary.map(s => (
                      <tr key={`${s.periodYear}-${s.periodQuarter}`} className="border-t border-gray-100">
                        <td className="py-1.5">{s.periodYear || '—'} Q{s.periodQuarter || '—'}</td>
                        <td className="text-right text-emerald-700">{formatCurrency(s.earnedCommission || 0)}</td>
                        <td className="text-right text-slate-700">{formatCurrency(s.openingMigrated || 0)}</td>
                        <td className="text-right text-amber-700">{formatCurrency(s.redeemed || 0)}</td>
                        <td className="text-right text-indigo-700">{formatCurrency(s.adjustments || 0)}</td>
                        <td className="text-right text-gray-500">{formatCurrency(s.uncategorized || 0)}</td>
                        <td className="text-right font-semibold">{formatCurrency(s.net)}</td>
                        <td className="text-right text-gray-500">{s.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Manual entry form */}
            <form onSubmit={submitEntry} data-testid="credits-entry-form" className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Add manual ledger entry</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <LabelInput label="Amount (positive = credit, negative = redeem)" required>
                  <input data-testid="credits-form-amount" type="number" step="0.01" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className={inputClass} />
                </LabelInput>
                <LabelInput label="Effective date" required>
                  <input data-testid="credits-form-effdate" type="date" required value={form.effectiveDate} onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} className={inputClass} />
                </LabelInput>
                <LabelInput label="Reason" required>
                  <select data-testid="credits-form-reason" required value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} className={inputClass}>
                    <option value="">Choose…</option>
                    {REASON_OPTIONS.map(r => <option key={r} value={r}>{r} — {REASON_GROUPS[r].label}</option>)}
                  </select>
                </LabelInput>
                <LabelInput label="Type (optional)">
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className={inputClass}>
                    <option value="">—</option>
                    {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </LabelInput>
                <LabelInput label="Source system">
                  <select value={form.sourceSystem} onChange={e => setForm(f => ({ ...f, sourceSystem: e.target.value }))} className={inputClass}>
                    {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </LabelInput>
                <LabelInput label="Status">
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={inputClass}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </LabelInput>
                <LabelInput label="Source reference">
                  <input value={form.sourceReference} onChange={e => setForm(f => ({ ...f, sourceReference: e.target.value }))} placeholder="e.g. HS-2024-0001" className={inputClass} />
                </LabelInput>
                <LabelInput label="Note (required if no source reference)" wide>
                  <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} className={inputClass} />
                </LabelInput>
              </div>
              <div>
                <button data-testid="credits-form-submit" disabled={saving} type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-900 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Add entry'}
                </button>
              </div>
            </form>

            {/* Transaction history */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Transactions ({sortedTx.length})</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase"><tr>
                  <th className="px-3 py-2 text-left">Effective</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Note</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-center">Status</th>
                </tr></thead>
                <tbody>
                  {sortedTx.map(tx => {
                    const g = REASON_GROUPS[tx.reason];
                    return (
                      <tr key={tx._id} className={`border-t border-gray-100 ${tx.status === 'VOID' ? 'line-through opacity-50' : ''}`}>
                        <td className="px-3 py-2 font-mono text-xs">{tx.effectiveDate || tx.created_at?.slice(0, 10) || '—'}</td>
                        <td className={`px-3 py-2 ${g?.color || 'text-gray-600'}`}>{g?.label || tx.reason || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{tx.sourceSystem || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">{tx.sourceReference || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{tx.note || '—'}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${Number(tx.amount) < 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{formatCurrency(tx.amount)}</td>
                        <td className="px-3 py-2 text-center text-xs">{tx.status || 'CONFIRMED'}</td>
                      </tr>
                    );
                  })}
                  {!sortedTx.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400 text-sm">No transactions</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const inputClass = 'w-full px-2 py-1.5 text-sm rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white';

function LabelInput({ label, required, wide, children }) {
  return (
    <label className={`block text-xs ${wide ? 'md:col-span-3' : ''}`}>
      <span className="text-gray-500 block mb-1">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}

function SummaryCard({ label, value, primary, negative }) {
  return (
    <div className={`rounded-xl border p-3 ${primary ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-gray-200'}`}>
      <p className={`text-[10px] uppercase tracking-wider font-semibold ${primary ? 'text-slate-300' : 'text-gray-500'}`}>{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${negative && value !== 0 ? 'text-amber-400' : ''}`}>{formatCurrency(value || 0)}</p>
    </div>
  );
}
